import userPreferences from "./userPreferences";
import { PREFERENCE_FAMILIES, resolveScope } from "./userPreferences.registry";
import { USER_SCOPE, savePreference, viewColumnsPrefKey } from "./userPreferencesApi";

/**
 * SPEC-0061 §4.4 — one-time import of the localStorage preferences into the
 * server store, at the first login after the release.
 *
 * For every family of the registry: read the local keys that still exist, map
 * them to the family's (scope, prefKey), PUT when the server has no document
 * yet, and remove the local keys only after a successful PUT (a failure leaves
 * them for the next login). A flag document user / migration:local-prefs-v1 is
 * written once every family went through; `view_prefs.*` documents of another
 * account are left for that account's own login.
 */
const VIEW_PREFS_PREFIX = "view_prefs.";
const FLAG = PREFERENCE_FAMILIES.migration;
const GRID_COLUMNS = PREFERENCE_FAMILIES.legacy_grid_columns;
const COLUMNS_STATE_SUFFIX = "_columnsState";
const COLUMNS_SAVED_AT_SUFFIX = "_columnsSavedAt";
// Legacy keys of the column copies that did not carry the grid id.
const GRID_COLUMNS_ALIASES = {
  taskFocusSavedColumnsOrder: { gridId: "TaskFocusGantt", field: "columns" },
  taskFocusColumnsSavedAt: { gridId: "TaskFocusGantt", field: "savedAt" },
  myTasksSavedColumnsOrder: { gridId: "UserTasksGrid", field: "visibleIds" },
};

const localKeys = () => {
  try {
    return Object.keys(localStorage);
  } catch {
    return [];
  }
};

const readJson = (key) => {
  const raw = localStorage.getItem(key);
  if (raw == null) return { present: false };
  try {
    return { present: true, value: JSON.parse(raw) };
  } catch {
    return { present: true, value: raw, raw: true };
  }
};

const isObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const matchingLegacyKeys = (family, keys) => {
  const { keys: exact = [], prefixes = [], suffixes = [] } = family.legacy;
  const claimedByExactElsewhere = new Set(
    Object.values(PREFERENCE_FAMILIES)
      .filter((f) => f !== family)
      .flatMap((f) => f.legacy.keys || [])
  );
  return keys.filter(
    (key) =>
      exact.includes(key) ||
      (!claimedByExactElsewhere.has(key) &&
        (prefixes.some((p) => key.startsWith(p)) || suffixes.some((s) => key.endsWith(s))))
  );
};

// The per-grid copies of the « custom layout » grids: `<gridId>_columnsState`
// (the column entries) + `<gridId>_columnsSavedAt`, plus the aliases above.
// One document per grid: { columns, savedAt, visibleIds }.
const MULTI_PROJECT_GANTT_GRID_ID = "MultiProjectGantt";
const LEGACY_TIMELINE_VISIBILITY_KEY = "columnsForTimeline-V9";

const collectGridColumns = (present, documents, corrupt) => {
  const byGrid = new Map();
  const docOf = (gridId) => {
    if (!byGrid.has(gridId)) byGrid.set(gridId, { state: {}, keys: [] });
    return byGrid.get(gridId);
  };
  // Before its own preset existed, the multi-project Gantt's layout was the full
  // timeline's visibility list (what seedPresetFromLegacyLayout carried over once).
  // The key stays with timeline_view (the full timeline still reads it).
  if (!present.includes(`${MULTI_PROJECT_GANTT_GRID_ID}${COLUMNS_STATE_SUFFIX}`)) {
    const { value } = readJson(LEGACY_TIMELINE_VISIBILITY_KEY);
    if (Array.isArray(value) && value.length) docOf(MULTI_PROJECT_GANTT_GRID_ID).state.columns = value;
  }
  present.forEach((key) => {
    const alias = GRID_COLUMNS_ALIASES[key];
    const gridId = alias
      ? alias.gridId
      : key.endsWith(COLUMNS_STATE_SUFFIX)
      ? key.slice(0, -COLUMNS_STATE_SUFFIX.length)
      : key.endsWith(COLUMNS_SAVED_AT_SUFFIX)
      ? key.slice(0, -COLUMNS_SAVED_AT_SUFFIX.length)
      : null;
    if (!gridId) return;
    const field = alias ? alias.field : key.endsWith(COLUMNS_STATE_SUFFIX) ? "columns" : "savedAt";
    const { value } = readJson(key);
    const doc = docOf(gridId);
    if (field === "savedAt") {
      const savedAt = Number(value);
      if (Number.isFinite(savedAt) && savedAt > 0) doc.state.savedAt = savedAt;
      else corrupt.push(key);
    } else if (Array.isArray(value)) {
      doc.state[field] = value;
    } else {
      corrupt.push(key);
      return;
    }
    doc.keys.push(key);
  });
  byGrid.forEach((doc, gridId) => {
    const { state } = doc;
    if (!state.columns && !state.visibleIds) {
      corrupt.push(...doc.keys);
      return;
    }
    documents.push({ scope: USER_SCOPE, prefKey: GRID_COLUMNS.prefKeyFor(gridId), state, keys: doc.keys });
  });
};

/** The documents to import: [{ scope, prefKey, state, keys }] plus the corrupt keys to drop. */
const collect = ({ userId, portfolioId }) => {
  const keys = localKeys();
  const documents = [];
  const corrupt = [];
  const waiting = [];

  Object.values(PREFERENCE_FAMILIES).forEach((family) => {
    if (family === FLAG) return;
    const scope = resolveScope(family, { portfolioId });

    if (family === PREFERENCE_FAMILIES.view_columns) {
      if (!userId) return;
      const suffix = `.${userId}`;
      keys
        .filter((key) => key.startsWith(VIEW_PREFS_PREFIX) && key.endsWith(suffix))
        .forEach((key) => {
          const viewId = key.slice(VIEW_PREFS_PREFIX.length, key.length - suffix.length);
          const { value } = readJson(key);
          if (isObject(value) && Array.isArray(value.columns)) {
            documents.push({ scope: USER_SCOPE, prefKey: viewColumnsPrefKey(viewId), state: value, keys: [key] });
          } else corrupt.push(key);
        });
      return;
    }

    let present = matchingLegacyKeys(family, keys);
    if (family.legacy.perUser) {
      // `<key>-<sub>` entries: only the current account's, stored without the suffix.
      if (!userId) return;
      present = present.filter((key) => (family.legacy.keys || []).includes(key) || key.endsWith(`-${userId}`));
    }
    if (!present.length) return;
    if (!scope) {
      waiting.push(family.name);
      return;
    }
    if (family === GRID_COLUMNS) {
      collectGridColumns(present, documents, corrupt);
      return;
    }
    if (family === PREFERENCE_FAMILIES.gantt_view) {
      const key = present[0];
      const { value } = readJson(key);
      if (isObject(value)) documents.push({ scope, prefKey: family.prefKey, state: value, keys: [key] });
      else corrupt.push(key);
      return;
    }
    const values = {};
    present.forEach((key) => {
      const { value, raw } = readJson(key);
      const storedKey =
        family.legacy.perUser && key.endsWith(`-${userId}`) ? key.slice(0, -(userId.length + 1)) : key;
      values[storedKey] = raw ? { __raw: true, value } : value;
    });
    // keepLocal: the key is also a pre-login cache and stays (language).
    documents.push({ scope, prefKey: family.prefKey, state: { values }, keys: family.legacy.keepLocal ? [] : present });
  });

  return { documents, corrupt, waiting };
};

const removeKeys = (keys) =>
  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // storage unavailable — nothing to remove
    }
  });

export const importLocalPreferences = async ({ userId, portfolioId } = {}) => {
  const summary = { imported: 0, skipped: 0, failed: 0, flagged: false };
  if (!userPreferences.isLoaded(USER_SCOPE)) return summary;
  if (userPreferences.has(USER_SCOPE, FLAG.prefKey)) return summary;

  const { documents, corrupt, waiting } = collect({ userId, portfolioId });
  removeKeys(corrupt);

  await Promise.all(
    documents.map(async (document) => {
      if (userPreferences.has(document.scope, document.prefKey)) {
        summary.skipped += 1;
        removeKeys(document.keys);
        return;
      }
      try {
        const dto = await savePreference(document.scope, document.prefKey, document.state);
        userPreferences.adopt({ ...dto, scope: document.scope, prefKey: document.prefKey, state: document.state });
        removeKeys(document.keys);
        summary.imported += 1;
      } catch (error) {
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500 && status !== 409) {
          // The server refused the document itself (too large, malformed): a retry would refuse it
          // again at every login and hold the flag forever. Dropped, the defaults apply.
          summary.skipped += 1;
          removeKeys(document.keys);
          // eslint-disable-next-line no-console
          console.warn(`[user-preferences] import of ${document.scope}/${document.prefKey} refused (${status}); dropped`);
          return;
        }
        summary.failed += 1;
        // eslint-disable-next-line no-console
        console.warn(`[user-preferences] import of ${document.scope}/${document.prefKey} failed; kept locally`, error?.message);
      }
    })
  );

  if (summary.failed === 0 && waiting.length === 0) {
    try {
      const state = { importedAt: new Date().toISOString(), imported: summary.imported, skipped: summary.skipped };
      const dto = await savePreference(USER_SCOPE, FLAG.prefKey, state);
      userPreferences.adopt({ ...dto, scope: USER_SCOPE, prefKey: FLAG.prefKey, state });
      summary.flagged = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[user-preferences] import flag could not be written; the import re-runs next login", error?.message);
    }
  }
  return summary;
};

/**
 * On a user switch on the same browser: the previous account's local residue
 * must not be imported under the next account. Per-user `view_prefs.*`
 * documents are keyed by account and stay.
 */
export const discardLocalPreferences = () => {
  const keys = localKeys();
  Object.values(PREFERENCE_FAMILIES).forEach((family) => {
    if (family === PREFERENCE_FAMILIES.view_columns || family.legacy.keepLocal) return;
    removeKeys(matchingLegacyKeys(family, keys));
  });
};
